import React, { useMemo, useState } from 'react';
import PersonContactHover from '../../components/PersonContactHover';
import { sortRows } from '../../utils/userAnalytics';
import SortableTh from './SortableTh';

function UserActivityTab({ profiles }) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('totalActivity');
  const [sortDir, setSortDir] = useState('desc');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter(p =>
      p.name.toLowerCase().includes(q)
      || p.userType.toLowerCase().includes(q)
      || p.homeFacility.toLowerCase().includes(q)
    );
  }, [profiles, search]);

  const sorted = useMemo(
    () => sortRows(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir]
  );

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'userType' || key === 'homeFacility' || key === 'lastActiveDate' ? 'asc' : 'desc');
    }
  };

  const totals = useMemo(() => ({
    users: profiles.length,
    logins: profiles.reduce((s, p) => s + p.logins, 0),
    messages: profiles.reduce((s, p) => s + p.messagesSent, 0),
    referrals: profiles.reduce((s, p) => s + p.referralTouches, 0),
  }), [profiles]);

  return (
    <div className="users-tab-panel">
      <div className="users-kpi-row">
        <div className="users-kpi"><span className="users-kpi-val">{totals.users.toLocaleString()}</span><span className="users-kpi-label">Active users</span></div>
        <div className="users-kpi"><span className="users-kpi-val">{totals.logins.toLocaleString()}</span><span className="users-kpi-label">Total logins</span></div>
        <div className="users-kpi"><span className="users-kpi-val">{totals.messages.toLocaleString()}</span><span className="users-kpi-label">Messages sent</span></div>
        <div className="users-kpi"><span className="users-kpi-val">{totals.referrals.toLocaleString()}</span><span className="users-kpi-label">Referral touches</span></div>
      </div>

      <div className="users-tab-toolbar">
        <input
          type="search"
          className="users-search"
          placeholder="Search users…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="users-sort">
          Sort by
          <select
            value={sortKey}
            onChange={(e) => {
              setSortKey(e.target.value);
              setSortDir(e.target.value === 'name' || e.target.value === 'lastActiveDate' ? 'asc' : 'desc');
            }}
          >
            <option value="totalActivity">Total activity</option>
            <option value="logins">Logins</option>
            <option value="messagesSent">Messages</option>
            <option value="referralTouches">Referrals</option>
            <option value="loginDays">Active days</option>
          </select>
        </label>
      </div>

      <div className="users-table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <SortableTh label="User" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableTh label="Type" sortKey="userType" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableTh label="Home facility" sortKey="homeFacility" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableTh label="Logins" sortKey="logins" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
              <SortableTh label="Days" sortKey="loginDays" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
              <SortableTh label="Messages" sortKey="messagesSent" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
              <SortableTh label="Referrals" sortKey="referralTouches" activeKey={sortKey} dir={sortDir} onSort={toggleSort} className="num" />
              <SortableTh label="Last active" sortKey="lastActiveDate" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0, 500).map(p => (
              <tr key={p.userId}>
                <td><PersonContactHover userId={Number(p.userId)}>{p.name || `User ${p.userId}`}</PersonContactHover></td>
                <td>{p.userType || '—'}</td>
                <td title={p.homeFacility}>{p.homeFacility || '—'}</td>
                <td className="num">{p.logins.toLocaleString()}</td>
                <td className="num">{p.loginDays}</td>
                <td className="num">{p.messagesSent.toLocaleString()}</td>
                <td className="num">{p.referralTouches.toLocaleString()}</td>
                <td>{p.lastActiveDate || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length > 500 && (
        <p className="users-footnote">Showing top 500 of {sorted.length.toLocaleString()} users by selected sort.</p>
      )}
    </div>
  );
}

export default UserActivityTab;
